import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PatientSectionLayout } from './PatientSectionLayout';
import { patientsService, PatientMealPlan } from '../../services/patients/patientsService';
import { getCurrentDoctor } from '../../services/doctor-onboarding/doctorOnboardingService';

const PatientDietPlansPage: React.FC = () => {
  const navigate = useNavigate();
  const { patientId } = useParams<{ patientId: string }>();
  const numericPatientId = patientId ? Number(patientId) : NaN;
  const validId = !Number.isNaN(numericPatientId) && numericPatientId > 0;

  const [allMealPlans, setAllMealPlans] = useState<PatientMealPlan[]>([]);
  const [mealPlanLoading, setMealPlanLoading] = useState(false);
  const [mealPlanError, setMealPlanError] = useState<string | null>(null);
  const [currentDoctorId, setCurrentDoctorId] = useState<number | null>(null);
  const [deletingGoalId, setDeletingGoalId] = useState<number | null>(null);

  const loadMealPlans = useCallback(async () => {
    if (!validId) return;
    try {
      setMealPlanLoading(true);
      setMealPlanError(null);
      const allPlans = await patientsService.getAllPatientMealPlans(numericPatientId);
      setAllMealPlans(allPlans);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setAllMealPlans([]);
        return;
      }
      setMealPlanError(err?.response?.data?.detail || err?.message || 'Failed to load meal plans');
    } finally {
      setMealPlanLoading(false);
    }
  }, [numericPatientId, validId]);

  useEffect(() => {
    getCurrentDoctor().then((doc) => setCurrentDoctorId(doc?.id ?? null));
  }, []);

  useEffect(() => {
    loadMealPlans();
  }, [loadMealPlans]);

  const handleDeleteMealPlan = useCallback(
    async (plan: PatientMealPlan) => {
      if (!patientId || plan.createdByDoctorId !== currentDoctorId) return;
      if (!window.confirm(`Delete diet plan "${plan.goalName}"? This cannot be undone.`)) return;
      try {
        setDeletingGoalId(plan.goalId);
        await patientsService.deletePatientMealPlan(numericPatientId, plan.goalId);
        await loadMealPlans();
      } catch (err: any) {
        setMealPlanError(err?.response?.data?.detail || err?.message || 'Failed to delete plan');
      } finally {
        setDeletingGoalId(null);
      }
    },
    [patientId, currentDoctorId, numericPatientId, loadMealPlans]
  );

  const sortedPlans = [...allMealPlans].sort((a, b) => {
    if (a.isActive && !b.isActive) return -1;
    if (!a.isActive && b.isActive) return 1;
    if (a.effectiveAt && b.effectiveAt) {
      return new Date(b.effectiveAt).getTime() - new Date(a.effectiveAt).getTime();
    }
    if (a.effectiveAt) return -1;
    if (b.effectiveAt) return 1;
    return 0;
  });

  return (
    <PatientSectionLayout section="diet-plans">
      <section className="dashboard-section patient-tabs-section">
        <div className="section-title-row">
          <h2 className="section-title">Diet Plans</h2>
          <button
            type="button"
            className="primary-button"
            onClick={() => navigate(`/patients/${patientId}?tab=mealPlan&createPlan=1`)}
          >
            Create New Plan
          </button>
        </div>
        {mealPlanError && (
          <div className="error-message" style={{ marginBottom: 16 }}>{mealPlanError}</div>
        )}
        {mealPlanLoading ? (
          <div className="loading-message">Loading diet plans...</div>
        ) : sortedPlans.length === 0 ? (
          <div className="no-appointments">No diet plans yet. Create one to get started.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 24 }}>
            {sortedPlans.map((plan) => (
              <div
                key={plan.goalId}
                onClick={() => navigate(`/patients/${patientId}/diet-plan/${plan.goalId}`)}
                style={{
                  padding: 16,
                  border: plan.isActive ? '2px solid #6366f1' : '2px solid #e5e7eb',
                  borderRadius: 12,
                  backgroundColor: plan.isActive ? '#fef2f2' : '#ffffff',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: plan.isActive ? '0 2px 8px rgba(220, 38, 38, 0.1)' : 'none',
                }}
                onMouseEnter={(e) => {
                  if (!plan.isActive) {
                    e.currentTarget.style.borderColor = '#d1d5db';
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!plan.isActive) {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.backgroundColor = '#ffffff';
                  } else {
                    e.currentTarget.style.borderColor = '#6366f1';
                    e.currentTarget.style.backgroundColor = '#fef2f2';
                  }
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <h4 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: plan.isActive ? '#6366f1' : '#111827' }}>
                      {plan.goalName}
                    </h4>
                    {plan.isActive && (
                      <span style={{
                        padding: '4px 10px',
                        backgroundColor: '#6366f1',
                        color: '#fff',
                        borderRadius: 6,
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                      }}>
                        Active
                      </span>
                    )}
                    {plan.planStatus === 'approved' && (
                      <span style={{ padding: '4px 10px', backgroundColor: '#059669', color: '#fff', borderRadius: 6, fontSize: 11, fontWeight: 600 }}>
                        Approved
                      </span>
                    )}
                    {plan.planStatus === 'in_progress' && (
                      <span style={{ padding: '4px 10px', backgroundColor: '#d97706', color: '#fff', borderRadius: 6, fontSize: 11, fontWeight: 600 }}>
                        In progress
                      </span>
                    )}
                  </div>
                  <div onClick={(e) => e.stopPropagation()}>
                    {plan.createdByDoctorId != null && plan.createdByDoctorId === currentDoctorId && !plan.isActive && (
                      <button
                        type="button"
                        className="secondary-button"
                        style={{ fontSize: 12, padding: '4px 10px', color: '#dc2626', borderColor: '#dc2626' }}
                        onClick={() => handleDeleteMealPlan(plan)}
                        disabled={deletingGoalId === plan.goalId}
                      >
                        {deletingGoalId === plan.goalId ? 'Deleting…' : 'Delete'}
                      </button>
                    )}
                  </div>
                </div>
                {plan.effectiveAt && (
                  <p style={{ margin: '4px 0', fontSize: 13, color: plan.isActive ? '#4338ca' : '#6b7280' }}>
                    Effective: {new Date(plan.effectiveAt).toLocaleDateString()}
                  </p>
                )}
                {plan.mealPlan?.totalCaloriesKcal && (
                  <p style={{ margin: '4px 0', fontSize: 14, color: plan.isActive ? '#4338ca' : '#374151', fontWeight: 500 }}>
                    {Math.round(plan.mealPlan.totalCaloriesKcal)} kcal/day
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </PatientSectionLayout>
  );
};

export default PatientDietPlansPage;
